import { useState, useEffect, useCallback } from "react";
import { APIResponse, APIError } from "@/types";

interface UseApiOptions {
  immediate?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: APIError) => void;
}

interface UseApiState<T> {
  data: T | null;
  isLoading: boolean;
  error: APIError | null;
}

interface UseApiReturn<T> extends UseApiState<T> {
  execute: (...args: any[]) => Promise<T>;
  reset: () => void;
  refetch: () => Promise<T>;
}

export function useApi<T>(
  apiCall: (...args: any[]) => Promise<APIResponse<T>>,
  options: UseApiOptions & { returnFullResponse: true },
): UseApiReturn<APIResponse<T> & Partial<T>>;
export function useApi<T>(
  apiCall: (...args: any[]) => Promise<APIResponse<T>>,
  options?: UseApiOptions & { returnFullResponse?: false },
): UseApiReturn<T>;
export function useApi<T = any>(
  apiCall: (...args: any[]) => Promise<APIResponse<T>>,
  options: UseApiOptions & { returnFullResponse?: boolean } = {},
): UseApiReturn<any> {
  const {
    immediate = false,
    onSuccess,
    onError,
    returnFullResponse = false,
  } = options;

  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [lastArgs, setLastArgs] = useState<any[]>([]);

  const execute = useCallback(
    async (...args: any[]): Promise<T> => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));
        setLastArgs(args);

        const response = await apiCall(...args);
        // If returnFullResponse is true, return the entire response (without the top-level wrapper)
        // Otherwise just return response.data as before
        const data = (returnFullResponse ? response : response.data) as T;

        setState({
          data,
          isLoading: false,
          error: null,
        });

        onSuccess?.(data);
        return data;
      } catch (error) {
        const apiError = error as APIError;
        setState({
          data: null,
          isLoading: false,
          error: apiError,
        });

        onError?.(apiError);
        throw apiError;
      }
    },
    [apiCall, onSuccess, onError, returnFullResponse],
  );

  const refetch = useCallback(() => {
    return execute(...lastArgs);
  }, [execute, lastArgs]);

  const reset = useCallback(() => {
    setState({
      data: null,
      isLoading: false,
      error: null,
    });
    setLastArgs([]);
  }, []);

  useEffect(() => {
    if (immediate) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate]);

  return {
    ...state,
    execute,
    refetch,
    reset,
  };
}

// Specialized hook for paginated APIs
type PaginatedParams = Record<string, unknown> & { page?: number };

export function usePaginatedApi<T = unknown>(
  apiCall: (params: PaginatedParams) => Promise<APIResponse<T[]>>,
  initialParams: PaginatedParams = {},
  options: { metaKey?: string } = {},
) {
  const { metaKey = "meta" } = options;
  const [params, setParams] = useState<PaginatedParams>(initialParams);
  const [allData, setAllData] = useState<T[]>([]);
  const [fullResponse, setFullResponse] = useState<any>(null);

  const {
    data: responseData,
    isLoading,
    error,
    execute,
  } = useApi(apiCall, {
    returnFullResponse: true, // Get the full response including meta
    onSuccess: (fullResp) => {
      // When returnFullResponse=true, fullResp is the entire APIResponse: {success, data, message}
      // The actual data is inside fullResp.data
      // Backend returns: {success, data: T[], pagination: {...}}
      let dataArray: T[];
      let meta: any;

      if (fullResp && typeof fullResp === "object") {
        // Extract from APIResponse structure
        const responseData = (fullResp as any).data;

        if (Array.isArray(responseData)) {
          // Backend returns: {success, data: T[], pagination: {...}}
          // Data is directly the array, pagination is at root level
          dataArray = responseData;
          meta = (fullResp as any)[metaKey];
        } else if (responseData && typeof responseData === "object") {
          // Nested structure: {success, data: {data: T[], pagination: {...}}}
          dataArray = responseData.data || [];
          meta = responseData[metaKey];
        } else {
          dataArray = [];
          meta = null;
        }
      } else {
        dataArray = [];
        meta = null;
      }

      if (params.page === 1) {
        setAllData(dataArray);
      } else {
        setAllData((prev) => [...prev, ...dataArray]);
      }

      setFullResponse(meta ? { [metaKey]: meta } : null);
    },
  });

  const loadMore = useCallback(() => {
    const meta = fullResponse?.[metaKey];
    if (meta?.has_next) {
      setParams((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  }, [fullResponse, metaKey]);

  const refresh = useCallback(() => {
    setParams((prev) => ({ ...prev, page: 1 }));
    setAllData([]);
  }, []);

  const updateParams = useCallback((newParams: any) => {
    // Only reset to page 1 if page is not explicitly provided
    const shouldResetPage = !("page" in newParams);
    setParams((prev) => ({
      ...prev,
      ...newParams,
      ...(shouldResetPage && { page: 1 }),
    }));
    setAllData([]);
  }, []);

  useEffect(() => {
    execute(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return {
    data: allData,
    pagination: fullResponse?.[metaKey],
    isLoading,
    error,
    loadMore,
    refresh,
    updateParams,
    hasMore: fullResponse?.[metaKey]?.has_next || false,
  };
}

// Hook for real-time data updates
export function usePolling<T = any>(
  apiCall: () => Promise<APIResponse<T>>,
  interval: number = 30000,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const { data, isLoading, error, execute } = useApi(apiCall);

  useEffect(() => {
    if (!enabled) return;

    // Initial fetch
    execute();

    // Set up polling
    const intervalId = setInterval(() => {
      execute();
    }, interval);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, interval]);

  return { data, isLoading, error, refetch: execute };
}

// Hook for optimistic updates
export function useOptimisticUpdate<T = any>() {
  const [optimisticData, setOptimisticData] = useState<T | null>(null);
  const [isOptimistic, setIsOptimistic] = useState(false);

  const performOptimisticUpdate = useCallback(
    async <U>(
      optimisticValue: T,
      asyncOperation: () => Promise<U>,
    ): Promise<U> => {
      try {
        setOptimisticData(optimisticValue);
        setIsOptimistic(true);

        const result = await asyncOperation();

        setIsOptimistic(false);
        return result;
      } catch (error) {
        // Revert optimistic update on error
        setOptimisticData(null);
        setIsOptimistic(false);
        throw error;
      }
    },
    [],
  );

  const clearOptimistic = useCallback(() => {
    setOptimisticData(null);
    setIsOptimistic(false);
  }, []);

  return {
    optimisticData,
    isOptimistic,
    performOptimisticUpdate,
    clearOptimistic,
  };
}

// Hook for debounced API calls
export function useDebouncedApi<T = any>(
  apiCall: (...args: any[]) => Promise<APIResponse<T>>,
  delay: number = 300,
) {
  const [debouncedArgs, setDebouncedArgs] = useState<any[] | null>(null);
  const { data, isLoading, error, execute } = useApi(apiCall);

  useEffect(() => {
    if (!debouncedArgs) return;

    const timer = setTimeout(() => {
      execute(...debouncedArgs);
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedArgs, delay]);

  const debouncedExecute = useCallback((...args: any[]) => {
    setDebouncedArgs(args);
  }, []);

  return {
    data,
    isLoading,
    error,
    execute: debouncedExecute,
  };
}
